import './TextField.scss';

import PropTypes from 'prop-types';
import React, {
  forwardRef,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import mergeRefs from 'react-merge-refs';
import ReactSelect, { components, createFilter } from 'react-select';
import CreatableSelect from 'react-select/creatable';

import { ReactComponent as AlertIcon } from 'assets/icons/alert.svg';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';
import cx from 'utils/cx';

const clearStyles = (noBorder) => ({
  control: () => ({}),
  valueContainer: (provided) => ({
    ...provided,
    padding: '0',
  }),
  dropdownIndicator: () => ({
    display: 'flex',
    color: '#BDBDBD',
    paddingRight: !noBorder ? '8px' : '0',
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#BDBDBD',
    whiteSpace: 'nowrap',
  }),
  singleValue: (provided) => ({
    ...provided,
    right: noBorder ? '0' : 'initial',
    maxWidth: 'none',
  }),
  indicatorSeparator: () => ({}),
  input: (provided) => provided,
  menu: () => ({}),
  menuList: () => ({}),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  option: () => ({}),
  noOptionsMessage: () => ({}),
});

const Control = ({ children, selectProps, ...rest }) => {
  const { showSearch, category } = selectProps;

  if (showSearch) {
    return (
      <components.Control selectProps={selectProps} {...rest}>
        <div className="prefix">
          <SearchIcon />
        </div>
        {category && (
          <span className="category-prefix">
            {category}
            &nbsp;
          </span>
        )}
        {children}
      </components.Control>
    );
  }

  return (
    <components.Control selectProps={selectProps} {...rest}>
      {children}
    </components.Control>
  );
};

Control.propTypes = {
  children: PropTypes.node.isRequired,
  selectProps: PropTypes.object.isRequired,
};

const Select = forwardRef(
  (
    {
      value,
      fieldRef,
      className,
      label,
      required,
      isSearchable,
      noBorder,
      placeholder,
      category,
      readOnly,
      options,
      onChange,
      error,
      defaultOptions,
      isCreatable,
      ...rest
    },
    ref,
  ) => {
    const innerRef = useRef();
    const { t } = useTranslation('common');
    const [menuIsOpen, setMenuIsOpen] = useState(false);

    const onInputChange = (options, test) => {
      if (test.action === 'menu-close') {
        setMenuIsOpen(false);
        innerRef.current.blur();
      }
    };

    const openMenu = () => {
      if (!readOnly) {
        innerRef.current.focus();
        setMenuIsOpen(true);
      }
    };

    const filterOptions = (option) =>
      defaultOptions.indexOf(+option.value) !== -1;

    const SelectComponent = useMemo(() => {
      if (isCreatable) {
        return CreatableSelect;
      }
      return ReactSelect;
    }, []);

    return (
      <div className="form-group select-form-group">
        {label && <span className={cx('label', { required })}>{label}</span>}
        <SelectComponent
          ref={mergeRefs([ref, innerRef, fieldRef])}
          className={cx(
            { 'no-border': noBorder, error, 'select--is-disabled': readOnly },
            className,
          )}
          classNamePrefix="select"
          placeholder={placeholder}
          styles={clearStyles(noBorder)}
          isSearchable={isSearchable && !readOnly}
          showSearch={isSearchable}
          {...rest}
          components={{ Control }}
          value={
            value === null
              ? null
              : options.find((c) => `${c.value}` === `${value}`)
          }
          options={options}
          onChange={onChange}
          noOptionsMessage={() => t('noResults')}
          menuIsOpen={menuIsOpen}
          onFocus={openMenu}
          onInputChange={onInputChange}
          filterOption={(option, input) =>
            createFilter()(option, input) || filterOptions(option)
          }
          isClearable={false}
          formatCreateLabel={(inputValue) => inputValue}
        />
        {!!error && typeof error !== 'boolean' && (
          <div className="form-message-wrapper">
            <AlertIcon />
            <span>
              {error.type === 'required'
                ? error.message || t('isRequired')
                : error.message}
            </span>
          </div>
        )}
      </div>
    );
  },
);

Select.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  className: PropTypes.string,
  label: PropTypes.string,
  required: PropTypes.bool,
  error: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.string,
    PropTypes.bool,
  ]),
  isSearchable: PropTypes.bool,
  noBorder: PropTypes.bool,
  placeholder: PropTypes.string,
  category: PropTypes.string,
  options: PropTypes.array,
  onChange: PropTypes.func,
  readOnly: PropTypes.bool,
  defaultOptions: PropTypes.array,
  fieldRef: PropTypes.func.isRequired,
  isCreatable: PropTypes.bool,
};

Select.defaultProps = {
  value: undefined,
  className: null,
  label: '',
  required: false,
  error: null,
  isSearchable: false,
  noBorder: false,
  placeholder: '',
  category: '',
  options: [],
  onChange: () => {},
  readOnly: false,
  defaultOptions: [],
  isCreatable: false,
};

const SelectContainer = forwardRef(
  (
    {
      name,
      rules,
      value,
      defaultValue,
      className,
      label,
      required,
      isSearchable,
      noBorder,
      placeholder,
      category,
      readOnly,
      options,
      onChange,
      error,
      defaultOptions,
      allOption,
      ...rest
    },
    ref,
  ) => {
    const { setValue } = useFormContext();
    const {
      field,
      fieldState: { error: fieldError },
    } = useController({
      name,
      rules: { required, ...rules },
      defaultValue,
    });

    const errorGlobal = error || fieldError;

    const onValueChange = useCallback(
      (val) => {
        if (val.value === -1) {
          field.onChange(null);
          onChange(null);
        } else {
          field.onChange(val.value);
          onChange(val.value);
        }
      },
      [onChange, field, setValue, name],
    );

    const getOptions = useMemo(
      () =>
        allOption ? [{ label: allOption, value: -1 }, ...options] : options,
      [allOption, options],
    );

    return (
      <Select
        ref={ref}
        fieldRef={field.ref}
        value={typeof value !== 'undefined' ? value : field.value}
        className={className}
        label={label}
        required={required}
        isSearchable={isSearchable}
        noBorder={noBorder}
        placeholder={placeholder}
        category={category}
        readOnly={readOnly}
        isDisabled={readOnly}
        options={getOptions}
        onChange={onValueChange}
        error={errorGlobal}
        defaultOptions={defaultOptions}
        {...rest}
      />
    );
  },
);

SelectContainer.propTypes = {
  name: PropTypes.string,
  rules: PropTypes.object,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  className: PropTypes.string,
  label: PropTypes.string,
  required: PropTypes.bool,
  error: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.string,
    PropTypes.bool,
  ]),
  isSearchable: PropTypes.bool,
  noBorder: PropTypes.bool,
  placeholder: PropTypes.string,
  category: PropTypes.string,
  options: PropTypes.array,
  onChange: PropTypes.func,
  readOnly: PropTypes.bool,
  defaultOptions: PropTypes.array,
  allOption: PropTypes.string,
};

SelectContainer.defaultProps = {
  name: '',
  rules: {},
  value: undefined,
  defaultValue: '',
  className: null,
  label: '',
  required: false,
  error: null,
  isSearchable: false,
  noBorder: false,
  placeholder: '',
  category: '',
  options: [],
  onChange: () => {},
  readOnly: false,
  defaultOptions: [],
  allOption: '',
};

export default SelectContainer;
